import React, { useMemo } from 'react';
import { useParams } from 'react-router';
import classNames from 'classnames';
import { ApolloQueryResult } from '@apollo/client';
import unionBy from 'lodash/unionBy';
import _isEmpty from 'lodash/isEmpty';
import { PromotionAppliedStatus } from '@atlassian/cssxp-types-promotion';
import { TabPanel, Tabs, useFeatureToggle } from '@css-xp/base';
import {
  EntitlementDetails,
  LinkedDestinationEntitlement,
  LinkedEntitlementTypes,
  LinkedProductTypes,
  ChargeQuantity,
  Maybe,
  RelationshipPathType,
  RelatedEntitlementDetails,
  BillingFrequency,
  FetchInvoiceGroupDetailsPpcQuery,
  FetchSandboxParentQuery,
} from '@css-xp/graphtypes/src/generated-types-new';
import { currentEntitlementType, getParentContainerId } from '@css-xp/base/entitlement-utils';
import { ENABLE_DESIGN_ITERATION_1, refundedTabNameKey, STATSIG_FEATURE_GATES } from '@css-xp/ppc-constants';
import {
  isEnterpriseOffering,
  isLoomProduct,
  isRovoOrgProduct,
  isEnterpriseInstanceOffering,
  isCollectionProductEntitlement,
  isCollectionInstanceEntitlement,
  isMultiInstanceProductOffering,
  isMultiInstanceOffering,
} from '@css-xp/ppc-utils';
import { InvoiceTable } from '@css-xp/ppc-invoice-table';
import {
  QuoteTableContainer,
  defaultQuotesFilterOptions,
  hiddenTabsQuoteFilterOptions,
  QUOTE_TABLE_COLUMNS,
} from '@css-xp/ppc-quote-table';
import { OrderHistoryTable } from '@css-xp/ppc-order-history-table';
import { ScheduledChangesTab } from '../scheduled-changes-tab/scheduled-changes-tab';
import { LinkedEntitlementsTab } from '../app-tab/linked-entitlements-tab';
import { ProductUsageTable } from '../product-usage-tab/product-usage-table';
import { ProductUsageGraphHistoryTable } from '../product-usage-tab/product-usage-graph-history-table';
import { CacheEntitlementPageTabsGraphqlQuery } from './cache-entitlement-page-tabs-graphql-query';
import DiscountTable from '../discount-table/discount-table';
import { AddOnsTab } from '../add-ons-tab/add-ons-tab';
import { EnabledOnTab } from '../enabled-on-tab/enabled-on-tab';
import { ProductInstances } from '../product-instances/product-instances';
import { SandboxTab } from '../sandbox-tab/sandbox-tab';
import { EntitlementSummary } from '../entitlement-summary/entitlement-summary';
import { LinkedEntitlementsTabV2 } from '../linked-entitlements-tab-v2/linked-entitlements-tab-v2';
import {
  EntitlementPageTabsColumn,
  EntitlementPageTabsColumnConfiguration,
  EntitlementPageTabsColumnConfigurationV2,
} from './table-column-configuration';
import './entitlement-page-tabs.scss';

interface EntitlementPageTabProps {
  entitlementDetails: EntitlementDetails;
  parentProductEntitlements: LinkedDestinationEntitlement[];
  refetch: () => Promise<ApolloQueryResult<unknown>>;
  invoiceGroupDetails: ApolloQueryResult<FetchInvoiceGroupDetailsPpcQuery | undefined>;
  shouldShowAddOnDetails?: boolean;
  sandboxParentDetails?: {
    parent: FetchSandboxParentQuery | undefined;
    loading: boolean;
  };
  openFeedbackWindow?: () => void;
}

export const EntitlementPageTabs = ({
  entitlementDetails,
  parentProductEntitlements,
  refetch,
  invoiceGroupDetails,
  shouldShowAddOnDetails,
  sandboxParentDetails,
  openFeedbackWindow,
}: EntitlementPageTabProps): JSX.Element | null => {
  const { txaId } = useParams<{ txaId: string }>();
  const [isEnabled] = useFeatureToggle();
  const isDesignIteration1Enabled = isEnabled(ENABLE_DESIGN_ITERATION_1);
  const isAddOnsTabEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_ADD_ONS);
  const isLoomSupportEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_LOOM_SUPPORT);
  const isRovoPhase2Enabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_ROVO_IN_PPC_PHASE2);
  const isJPDAsJiraFamilyEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_JPD_AS_JIRA_FAMILY);
  const isTWCPhase1Enabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_TWC_SUPPORT_PHASE_1);
  const isTWCPhase2Enabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_TWC_SUPPORT_PHASE_2);
  const isSandboxSupportForSubscriptionsEnabled = isEnabled(
    STATSIG_FEATURE_GATES.ENABLE_SANDBOX_SUPPORT_FOR_SUBSCRIPTIONS
  );
  const isFutureDatedEntitlementEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_SUPPORT_FOR_FUTURE_DATED_ENTITLEMENTS);
  const isSandboxDecouplingEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_SANDBOX_DECOUPLING);
  const isStatusPageEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_STATUSPAGE);
  const isDefaultFiltersEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_DEFAULT_FILTERS);
  const isAppificationEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_APPIFICATION);
  const isMultiInstanceAppsEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_MULTI_INSTANCE_APPS);
  const isNewHWBGraphChangesEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_HWB_UPGRADE);
  const isAchPaymentMethodFFEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_ACH_DIRECT_DEBIT);
  const isCollectionProduct = isCollectionProductEntitlement(entitlementDetails?.productId);

  const shouldRenderIncludedProductsTab = isTWCPhase1Enabled && isCollectionProduct;

  const shouldRenderIncludedInTab =
    isTWCPhase1Enabled && isCollectionInstanceEntitlement(entitlementDetails?.relatesFromEntitlements);

  const isEnterpriseParentProduct = isEnterpriseOffering(entitlementDetails?.edition);
  const isEnterpriseInstanceEntitlement = isEnterpriseInstanceOffering(entitlementDetails?.edition);
  const shouldRenderEnterpriseInstanceTab = isEnterpriseParentProduct && !isCollectionProduct;
  const isParentAppInstanceEntitlement =
    isMultiInstanceAppsEnabled &&
    entitlementDetails?.productId &&
    isMultiInstanceProductOffering(entitlementDetails?.offering, entitlementDetails.productId);
  const isMultiInstanceAppInstanceProduct =
    isMultiInstanceAppsEnabled &&
    entitlementDetails?.productId &&
    isMultiInstanceOffering(entitlementDetails?.offering, entitlementDetails.productId);
  const showSandboxTab = useMemo(() => {
    const hasSandboxRelationship = (relatesToEntitlements: Maybe<RelatedEntitlementDetails>[]) =>
      relatesToEntitlements.filter(
        (entitlement) =>
          entitlement?.relationshipType === RelationshipPathType.SANDBOX_DEPENDENCE ||
          entitlement?.relationshipType === RelationshipPathType.SANDBOX_GRANT
      ).length > 0;

    if (entitlementDetails?.relatesToEntitlements?.length && !entitlementDetails?.isSandbox) {
      return hasSandboxRelationship(entitlementDetails?.relatesToEntitlements);
    }

    return false;
  }, [entitlementDetails]);

  const shouldRenderSandboxTab = isSandboxSupportForSubscriptionsEnabled && showSandboxTab;
  const shouldRenderLinkedProductsTab =
    isSandboxSupportForSubscriptionsEnabled && entitlementDetails?.isApp && entitlementDetails?.isSandbox;

  const highestUsageParentEntitlementId = useMemo(() => {
    return parentProductEntitlements.sort((a, b) => Number(b?.activeUsers ?? 0) - Number(a?.activeUsers ?? 0))?.[0]
      ?.entitlementId;
  }, [parentProductEntitlements]);

  const entitlementFilter = [
    {
      entitlementId: entitlementDetails?.id,
    },
  ];

  const orderHistoryFilterOptions = [
    {
      key: 'orderLineItemsEntitlementId',
      value: entitlementDetails?.id,
    },
  ];

  const quotesFilterOptions = {
    and: [{ key: 'entitlementId', value: entitlementDetails?.id }, ...defaultQuotesFilterOptions.and],
  };

  const hiddenTabsQuoteTabFilterOptions = {
    and: [{ key: 'entitlementId', value: entitlementDetails?.id }, ...hiddenTabsQuoteFilterOptions.and],
  };

  const renderInvoicesTab = () => {
    return (
      <InvoiceTable
        transactionAccountId={txaId}
        id={txaId}
        filters={entitlementFilter}
        shouldHideCustomiseView={isDesignIteration1Enabled}
        label="Invoices"
        showBillingProfileNumber={isDesignIteration1Enabled}
      />
    );
  };

  const renderRefundsTab = () => {
    return (
      <InvoiceTable
        transactionAccountId={txaId}
        id={txaId}
        filters={entitlementFilter}
        shouldHideCustomiseView={isDesignIteration1Enabled}
        label="Refunds"
        selectedInterfaceKey={refundedTabNameKey}
        showBillingProfileNumber={isDesignIteration1Enabled}
      />
    );
  };

  const renderOrderHistoryTab = () => {
    return (
      <OrderHistoryTable label={'Order history'} filters={orderHistoryFilterOptions} transactionAccountId={txaId} />
    );
  };

  const renderUserOrUsageHistoryTab = () => {
    const entitlementNextBillingDate =
      entitlementDetails?.latestPeriodEndDate ??
      entitlementDetails?.subscriptionTimestamps?.lastTrialEndTimestamp ??
      null;

    if (showUsageHistoryGraph || shouldShowLoomUsageHistoryGraph) {
      return (
        <ProductUsageGraphHistoryTable
          id={entitlementDetails?.id}
          transactionAccountId={txaId}
          tableHeading={userTabAndTableHeadingName}
          entitlementNextBillingDate={entitlementNextBillingDate}
          isDesignIteration1Enabled={isDesignIteration1Enabled}
          isNewHWBGraphChangesEnabled={isNewHWBGraphChangesEnabled}
          openFeedbackWindow={openFeedbackWindow}
        />
      );
    }

    return (
      <ProductUsageTable
        id={entitlementDetails?.id}
        highestUsageParentEntitlementId={highestUsageParentEntitlementId}
        transactionAccountId={txaId}
        chargeElement={entitlementDetails?.chargeElement}
        tableHeading={userTabAndTableHeadingName}
        isDesignIteration1Enabled={isDesignIteration1Enabled}
      />
    );
  };

  const renderDiscountsTab = () => (
    <TabPanel label={isDesignIteration1Enabled ? 'Discounts' : 'Active discounts'}>
      <DiscountTable promotions={appliedPromotionsList} isDesignIteration1Enabled={isDesignIteration1Enabled} />
    </TabPanel>
  );

  const renderScheduledChangesTab = () => (
    <ScheduledChangesTab
      changeAction={entitlementDetails?.scheduledChanges?.subscriptionScheduleAction || null}
      changeDate={entitlementDetails?.scheduledChanges?.nextChangeTimestamp || null}
      oldValues={{
        offering: entitlementDetails?.offering?.name || null,
        pricingPlan: entitlementDetails?.pricingPlan?.type,
        billingFrequency: entitlementDetails?.pricingPlan?.billingFrequency || null,
      }}
      newValues={{
        offering: entitlementDetails?.scheduledChanges?.offering?.name || null,
        pricingPlan: entitlementDetails?.scheduledChanges?.pricingPlan?.type || null,
        billingFrequency: entitlementDetails?.scheduledChanges?.pricingPlan?.billingFrequency || null,
      }}
      scheduledQuantity={(entitlementDetails?.scheduledChanges?.chargeQuantities ?? []) as ChargeQuantity[]}
      units={entitlementDetails?.unitCount}
      productId={entitlementDetails?.productId}
      isDesignIteration1Enabled={isDesignIteration1Enabled}
    />
  );

  const renderQuotesTab = () => (
    <TabPanel label="Quotes">
      <QuoteTableContainer
        transactionAccountId={txaId}
        label="Quotes"
        filterOptions={
          isDesignIteration1Enabled && !isDefaultFiltersEnabled ? hiddenTabsQuoteTabFilterOptions : quotesFilterOptions
        }
        columnsToShow={
          isDesignIteration1Enabled
            ? [
                QUOTE_TABLE_COLUMNS.QUOTE_NUMBER,
                QUOTE_TABLE_COLUMNS.STATUS,
                QUOTE_TABLE_COLUMNS.QUOTE_TOTAL,
                QUOTE_TABLE_COLUMNS.PARTNER_MARGIN,
                QUOTE_TABLE_COLUMNS.BILLING_PROFILE,
                QUOTE_TABLE_COLUMNS.CREATED,
                QUOTE_TABLE_COLUMNS.EXPIRES,
                QUOTE_TABLE_COLUMNS.CUSTOMER,
                QUOTE_TABLE_COLUMNS.SOLD_TO_ADDRESS,
                QUOTE_TABLE_COLUMNS.CREATED_BY,
                QUOTE_TABLE_COLUMNS.UPDATED_BY,
                QUOTE_TABLE_COLUMNS.CUSTOMER_CONTACTS,
                QUOTE_TABLE_COLUMNS.QUOTE_CONTACT,
                QUOTE_TABLE_COLUMNS.PO_NUMBER,
                QUOTE_TABLE_COLUMNS.LAST_UPDATED,
              ]
            : undefined
        }
        showBillingProfileNumberInBillingProfileColumn={isDesignIteration1Enabled}
      />
    </TabPanel>
  );

  const renderLinkedAppTab = () => (
    <LinkedEntitlementsTab
      id={getParentContainerId(entitlementDetails, undefined, isJPDAsJiraFamilyEnabled)}
      entitlementId={entitlementDetails?.id}
      currentEntitlementType={currentEntitlementType(entitlementDetails)}
      destinationEntitlementType={[LinkedEntitlementTypes.PROD_ENTITLEMENT_APP]}
      productType={entitlementDetails?.productType as LinkedProductTypes}
      findApps={true}
      findProducts={false}
      transactionAccountId={txaId}
      isDesignIteration1Enabled={isDesignIteration1Enabled}
      isFutureDatedEntitlementEnabled={isFutureDatedEntitlementEnabled}
      isMultiInstanceAppsEnabled={isMultiInstanceAppsEnabled}
    />
  );

  const renderLinkedSandboxAppTab = () => (
    <LinkedEntitlementsTab
      id={getParentContainerId(entitlementDetails, undefined, isJPDAsJiraFamilyEnabled)}
      entitlementId={entitlementDetails?.id}
      currentEntitlementType={currentEntitlementType(entitlementDetails)}
      destinationEntitlementType={[LinkedEntitlementTypes.SANDBOX_APP_ENTITLEMENT]}
      productType={entitlementDetails?.productType as LinkedProductTypes}
      findApps={true}
      findProducts={false}
      transactionAccountId={txaId}
      isDesignIteration1Enabled={isDesignIteration1Enabled}
      isFutureDatedEntitlementEnabled={isFutureDatedEntitlementEnabled}
    />
  );

  /** only show add-ons tab if add-ons are there */
  const isAddOnsAvailable = entitlementDetails?.relatesToEntitlements?.some(
    //@ts-ignore
    (item) => item?.relationshipType === 'ADDON_DEPENDENCE'
  );
  const shouldRenderAddOnsRelatedTab = isAddOnsTabEnabled && isAddOnsAvailable;

  const shouldShowLoomUsageHistoryGraph =
    isLoomSupportEnabled &&
    isLoomProduct(entitlementDetails?.productId) &&
    entitlementDetails?.billingFrequency === BillingFrequency.MONTHLY;

  const showUsageHistoryGraph =
    isNewHWBGraphChangesEnabled && entitlementDetails?.billingFrequency === BillingFrequency.MONTHLY;

  const shouldRenderParentsTab = isAddOnsTabEnabled && entitlementDetails?.isAddOn;

  const shouldRenderUsageHistoryTab = isAddOnsTabEnabled && entitlementDetails?.isAddOn;
  const shouldRenderSiteInstancesTab = isRovoPhase2Enabled && isRovoOrgProduct(entitlementDetails);

  /** apps tab to be shown only when the current entitlement is neither an app, an add-on, or a sandbox */
  const shouldRenderAppsTab = !(
    entitlementDetails?.isApp ||
    (isAddOnsTabEnabled && entitlementDetails?.isAddOn) ||
    (isSandboxSupportForSubscriptionsEnabled && entitlementDetails?.isSandbox)
  );

  const shouldRenderSandboxAppTab =
    isSandboxSupportForSubscriptionsEnabled &&
    !entitlementDetails?.isApp &&
    entitlementDetails?.isSandbox &&
    !isEnterpriseParentProduct;

  const commonPropsForTabs = useMemo(() => {
    return {
      entitlementId: entitlementDetails?.id,
      containerId: getParentContainerId(entitlementDetails),
      currentEntitlementType: currentEntitlementType(entitlementDetails),
      productType: entitlementDetails?.productType as LinkedProductTypes,
      transactionAccountId: txaId,
      isDesignIteration1Enabled,
    };
  }, [entitlementDetails, isDesignIteration1Enabled, txaId]);

  let userTabAndTableHeadingName =
    shouldRenderUsageHistoryTab || shouldShowLoomUsageHistoryGraph ? 'Usage history' : 'User history';

  let parentTabHeading = 'Parent';
  if (isDesignIteration1Enabled) {
    userTabAndTableHeadingName = showUsageHistoryGraph ? 'Usage log' : 'User log';
    if (entitlementDetails?.isAddOn) {
      parentTabHeading = 'Enabled-on';
    } else if (entitlementDetails?.isApp) {
      parentTabHeading = 'Installed-on';
    } else if (entitlementDetails?.isSandbox && !entitlementDetails?.isApp) {
      parentTabHeading = 'Linked product';
    }
  }

  let appTabHeading = 'Apps';
  if (isAppificationEnabled && !isParentAppInstanceEntitlement) {
    appTabHeading = 'Marketplace apps';
  } else if (isParentAppInstanceEntitlement) {
    appTabHeading = 'Included apps';
  }

  const tabsConfiguration = useMemo(
    () => [
      {
        ...commonPropsForTabs,
        label: 'Enterprise instances',
        shouldRenderTab: isTWCPhase1Enabled && shouldRenderEnterpriseInstanceTab,
        destinationEntitlementType: [LinkedEntitlementTypes.ENTERPRISE_INSTANCE],
        findProducts: false,
        findApps: false,
        skipOption: undefined,
        tableHeading: 'Enterprise instances',
        tableColumns: isDesignIteration1Enabled
          ? EntitlementPageTabsColumnConfigurationV2[EntitlementPageTabsColumn.ENTERPRISE_INSTANCES]
          : EntitlementPageTabsColumnConfiguration[EntitlementPageTabsColumn.ENTERPRISE_INSTANCES],
      },
      {
        ...commonPropsForTabs,
        label: 'Included products',
        shouldRenderTab: shouldRenderIncludedProductsTab,
        destinationEntitlementType: [LinkedEntitlementTypes.COLLECTION_INSTANCE],
        findProducts: true,
        findApps: false,
        skipOption: undefined,
        tableHeading: 'Included products',
        tableColumns: isDesignIteration1Enabled
          ? EntitlementPageTabsColumnConfigurationV2[EntitlementPageTabsColumn.INCLUDED_PRODUCTS]
          : EntitlementPageTabsColumnConfiguration[EntitlementPageTabsColumn.INCLUDED_PRODUCTS],
      },
      {
        ...commonPropsForTabs,
        label: 'Included in',
        shouldRenderTab: shouldRenderIncludedInTab,
        destinationEntitlementType: [LinkedEntitlementTypes.COLLECTION_PRODUCT],
        findProducts: true,
        findApps: false,
        skipOption: undefined,
        tableHeading: 'Included in',
        tableColumns: isDesignIteration1Enabled
          ? EntitlementPageTabsColumnConfigurationV2[EntitlementPageTabsColumn.INCLUDED_IN]
          : EntitlementPageTabsColumnConfiguration[EntitlementPageTabsColumn.INCLUDED_IN],
      },
      // tab for parent product of app & sandbox
      {
        ...commonPropsForTabs,
        label: parentTabHeading,
        shouldRenderTab:
          isDesignIteration1Enabled &&
          !shouldRenderLinkedProductsTab &&
          (entitlementDetails?.isApp || entitlementDetails?.isSandbox),
        destinationEntitlementType: [LinkedEntitlementTypes.PROD_ENTITLEMENT_PRODUCT],
        findProducts: true,
        findApps: false,
        skipOption: undefined,
        tableHeading: parentTabHeading,
        tableColumns: EntitlementPageTabsColumnConfigurationV2[EntitlementPageTabsColumn.INSTALLED_ON],
      },
      {
        ...commonPropsForTabs,
        containerId: undefined,
        label: 'Enterprise parent',
        shouldRenderTab: isDesignIteration1Enabled && isEnterpriseInstanceEntitlement,
        destinationEntitlementType: [LinkedEntitlementTypes.ENTERPRISE_PARENT],
        findProducts: true,
        findApps: false,
        skipOption: undefined,
        tableHeading: 'Enterprise parent',
        tableColumns: EntitlementPageTabsColumnConfigurationV2[EntitlementPageTabsColumn.INSTALLED_ON],
      },
      {
        ...commonPropsForTabs,
        containerId: undefined,
        currentEntitlementType: LinkedEntitlementTypes.MULTI_INSTANCE,
        label: 'Included in',
        shouldRenderTab: isMultiInstanceAppInstanceProduct,
        destinationEntitlementType: [LinkedEntitlementTypes.MULTI_INSTANCE_PRODUCT],
        findProducts: true,
        findApps: false,
        skipOption: undefined,
        tableHeading: 'Included in',
        tableColumns: isDesignIteration1Enabled
          ? EntitlementPageTabsColumnConfigurationV2[EntitlementPageTabsColumn.INCLUDED_IN]
          : EntitlementPageTabsColumnConfiguration[EntitlementPageTabsColumn.INCLUDED_IN],
      },
    ],
    [
      commonPropsForTabs,
      isDesignIteration1Enabled,
      isTWCPhase1Enabled,
      shouldRenderEnterpriseInstanceTab,
      shouldRenderIncludedInTab,
      shouldRenderIncludedProductsTab,
      isMultiInstanceAppInstanceProduct,
      isEnterpriseInstanceEntitlement,
      entitlementDetails,
      parentTabHeading,
      shouldRenderLinkedProductsTab,
    ]
  );

  const renderLinkedEntitlementsTabsV2 = () => {
    return tabsConfiguration.map((tabConfig) => {
      if (!tabConfig.shouldRenderTab) {
        return null;
      }
      return (
        <TabPanel label={tabConfig.label} key={tabConfig.entitlementId}>
          <LinkedEntitlementsTabV2
            entitlementId={tabConfig.entitlementId}
            containerId={tabConfig.containerId}
            currentEntitlementType={tabConfig.currentEntitlementType}
            destinationEntitlementType={tabConfig.destinationEntitlementType}
            productType={tabConfig.productType}
            findProducts={tabConfig.findProducts}
            findApps={tabConfig.findApps}
            skipOption={tabConfig.skipOption}
            transactionAccountId={tabConfig.transactionAccountId}
            isDesignIteration1Enabled={tabConfig.isDesignIteration1Enabled}
            tableHeading={tabConfig.tableHeading}
            tableColumns={tabConfig.tableColumns}
            isAppificationEnabled={isAppificationEnabled}
          />
        </TabPanel>
      );
    });
  };

  const renderEnterpriseInstancesTab = () => {
    return (
      <ProductInstances
        entitlementId={entitlementDetails?.id}
        entitlementType={currentEntitlementType(entitlementDetails)}
        productType={entitlementDetails?.productType}
        transactionAccountId={txaId}
        cloudProductDetailViewVersion={isDesignIteration1Enabled ? 2 : undefined}
        tableHeading="Enterprise instances"
        isFutureDatedEntitlementEnabled={isFutureDatedEntitlementEnabled}
        isDesignIteration1Enabled={isDesignIteration1Enabled}
      />
    );
  };

  const renderSiteIntancesTab = () => {
    return (
      <ProductInstances
        entitlementId={entitlementDetails?.id}
        entitlementType={currentEntitlementType(entitlementDetails)}
        productType={entitlementDetails?.productType}
        transactionAccountId={txaId}
        cloudProductDetailViewVersion={isDesignIteration1Enabled ? 2 : undefined}
        tableHeading="Site instances"
        searchLinkedEntitlementParams={{
          destinationEntitlementType: [
            LinkedEntitlementTypes.PROD_ENTITLEMENT_APP,
            LinkedEntitlementTypes.PROD_ENTITLEMENT_PRODUCT,
          ],
          findApps: true,
          findProducts: true,
        }}
        isFutureDatedEntitlementEnabled={isFutureDatedEntitlementEnabled}
        isDesignIteration1Enabled={isDesignIteration1Enabled}
      />
    );
  };

  const renderAddOnsTab = () => {
    return (
      <AddOnsTab
        id={entitlementDetails?.id}
        entitlementId={entitlementDetails?.id}
        currentEntitlementType={currentEntitlementType(entitlementDetails)}
        productType={entitlementDetails?.productType as LinkedProductTypes}
        transactionAccountId={txaId}
        isDesignIteration1Enabled={isDesignIteration1Enabled}
        isFutureDatedEntitlementEnabled={isFutureDatedEntitlementEnabled}
      />
    );
  };

  const renderParentsTab = () => {
    return (
      <EnabledOnTab
        id={entitlementDetails?.id}
        entitlementId={entitlementDetails?.id}
        currentEntitlementType={currentEntitlementType(entitlementDetails)}
        productType={entitlementDetails?.productType as LinkedProductTypes}
        transactionAccountId={txaId}
        isFutureDatedEntitlementEnabled={isFutureDatedEntitlementEnabled}
        isDesignIteration1Enabled={isDesignIteration1Enabled}
        isEnterpriseInstanceEntitlement={isEnterpriseInstanceEntitlement}
        isAddon={entitlementDetails?.isAddOn || false}
        parentProductEntitlements={parentProductEntitlements}
        isApp={entitlementDetails?.isApp || false}
        isSandbox={entitlementDetails?.isSandbox || false}
        sandboxParentDetails={sandboxParentDetails}
      />
    );
  };

  const renderSandboxTab = () => {
    return (
      <SandboxTab
        id={getParentContainerId(entitlementDetails)}
        entitlementId={entitlementDetails?.id}
        currentEntitlementType={currentEntitlementType(entitlementDetails)}
        destinationEntitlementType={[LinkedEntitlementTypes.SANDBOX_PRODUCT_ENTITLEMENT]}
        productType={entitlementDetails?.productType as LinkedProductTypes}
        findApps={false}
        findProducts={true}
        transactionAccountId={txaId}
        isDesignIteration1Enabled={isDesignIteration1Enabled}
        isSandboxDecouplingEnabled={isSandboxDecouplingEnabled}
        isFutureDatedEntitlementEnabled={isFutureDatedEntitlementEnabled}
      />
    );
  };

  const renderLinkedProducts = () => {
    return (
      <SandboxTab
        id={getParentContainerId(entitlementDetails)}
        entitlementId={entitlementDetails?.id}
        currentEntitlementType={LinkedEntitlementTypes.SANDBOX_APP_ENTITLEMENT}
        destinationEntitlementType={[LinkedEntitlementTypes.SANDBOX_PRODUCT_ENTITLEMENT]}
        productType={entitlementDetails?.productType as LinkedProductTypes}
        findApps={false}
        findProducts={true}
        tableHeading="Sandbox parent subscriptions"
        transactionAccountId={txaId}
        isDesignIteration1Enabled={isDesignIteration1Enabled}
        isFutureDatedEntitlementEnabled={isFutureDatedEntitlementEnabled}
      />
    );
  };

  const renderEntitlementSummary = () => {
    return (
      <EntitlementSummary
        entitlement={entitlementDetails}
        refetch={refetch}
        parentProductEntitlements={parentProductEntitlements as []}
        invoiceGroupDetails={invoiceGroupDetails}
        shouldShowAddOnDetails={shouldShowAddOnDetails}
        sandboxParentDetails={sandboxParentDetails}
        isDesignIteration1Enabled={isDesignIteration1Enabled}
        isTWCPhase2Enabled={isTWCPhase2Enabled}
        isManuallyBilledSupportEnabled={true}
        hideBanner
        isStatusPageEnabled={isStatusPageEnabled}
        shouldShowACHPaymentMethod={isAchPaymentMethodFFEnabled}
      />
    );
  };

  const activePromotions = (entitlementDetails as EntitlementDetails)?.promotions || [];
  const currentBillingFrequency = (entitlementDetails as EntitlementDetails)?.billingFrequency;

  const appliedPromotions = activePromotions.map((promotion) => {
    return {
      ...promotion,
      billingCycle: PromotionAppliedStatus.CURRENT,
      billingFrequency: currentBillingFrequency,
    };
  });

  const scheduledPromotions = (entitlementDetails as EntitlementDetails)?.scheduledChanges?.promotions || [];
  const scheduledBillingFrequency = (entitlementDetails as EntitlementDetails)?.scheduledChanges?.pricingPlan
    ?.billingFrequency;

  const nextBillingPromotions = scheduledPromotions.map((promotion) => {
    return {
      ...promotion,
      billingCycle: PromotionAppliedStatus.NEXT,
      billingFrequency: scheduledBillingFrequency,
    };
  });

  const appliedPromotionsList = unionBy(appliedPromotions, nextBillingPromotions, 'promotionInstanceId');

  if (_isEmpty(entitlementDetails)) {
    return null;
  }

  return (
    <>
      {isEnabled(STATSIG_FEATURE_GATES.ENABLE_GRAPHQL_CACHING) && (
        <CacheEntitlementPageTabsGraphqlQuery
          transactionAccountId={txaId}
          entitlementDetails={entitlementDetails}
          invoiceTableFilter={entitlementFilter}
          orderHistoryFilterOptions={orderHistoryFilterOptions}
          highestUsageParentEntitlementId={highestUsageParentEntitlementId}
        />
      )}
      <div className="entitlement-page-tabs">
        <Tabs
          className={classNames('tab-container', {
            'entitlement-detail-tab-container': isDesignIteration1Enabled,
          })}
        >
          {renderLinkedEntitlementsTabsV2()}
          {/* Migrated to LinkedEntitlementsTabV2 Configuration, cleanup after FF release */}
          {shouldRenderEnterpriseInstanceTab && !isTWCPhase1Enabled && (
            <TabPanel label="Enterprise instances">{renderEnterpriseInstancesTab()}</TabPanel>
          )}

          {/* Please use LinkedEntitlementsTabV2 for tabs configuration */}
          {shouldRenderSiteInstancesTab && <TabPanel label="Site instances">{renderSiteIntancesTab()}</TabPanel>}
          <TabPanel label="Scheduled changes" enabled={!!entitlementDetails?.scheduledChanges}>
            {renderScheduledChangesTab()}
          </TabPanel>
          {shouldRenderAddOnsRelatedTab && <TabPanel label="Add-ons">{renderAddOnsTab()}</TabPanel>}
          {shouldRenderParentsTab && <TabPanel label={parentTabHeading}>{renderParentsTab()}</TabPanel>}
          {shouldRenderAppsTab && <TabPanel label={appTabHeading}>{renderLinkedAppTab()}</TabPanel>}
          {shouldRenderSandboxAppTab && (
            <TabPanel label={isAppificationEnabled ? 'Marketplace apps' : 'Apps'}>
              {renderLinkedSandboxAppTab()}
            </TabPanel>
          )}
          {shouldRenderSandboxTab && <TabPanel label="Sandbox">{renderSandboxTab()}</TabPanel>}
          {shouldRenderLinkedProductsTab && (
            <TabPanel label={isDesignIteration1Enabled ? 'Installed-on' : 'Linked products'}>
              {renderLinkedProducts()}
            </TabPanel>
          )}
          {isDesignIteration1Enabled && renderQuotesTab()}
          {!isDesignIteration1Enabled && renderDiscountsTab()}
          <TabPanel label="Invoices">{renderInvoicesTab()}</TabPanel>
          {isDesignIteration1Enabled && <TabPanel label="Refunds">{renderRefundsTab()}</TabPanel>}
          <TabPanel label="Order history">{renderOrderHistoryTab()}</TabPanel>
          <TabPanel label={userTabAndTableHeadingName}>{renderUserOrUsageHistoryTab()}</TabPanel>
          {!isDesignIteration1Enabled && renderQuotesTab()}
          {isDesignIteration1Enabled && renderDiscountsTab()}
          {isDesignIteration1Enabled && <TabPanel label="Details">{renderEntitlementSummary()}</TabPanel>}
        </Tabs>
      </div>
    </>
  );
};

export default EntitlementPageTabs;
